import { format, startOfMonth, endOfMonth, subMonths } from "date-fns"

// ---------- dates ----------
export const ymd = d => format(new Date(d), "yyyy-MM-dd")

// ---------- files ----------
// Slug sigur pentru nume de fișier: fără diacritice, litere mici, cuvinte
// separate prin „-" (ex: "Auto Service SRL" -> "auto-service-srl").
export const fileSlug = text => (text || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")

// Nume descriptiv pentru PDF-ul unei facturi: factura-<firma>-<data>.pdf
// (ex: factura-auto-service-srl-2026-10-08.pdf). Întoarce "" dacă lipsește
// firma sau data, lăsând apelantul să cadă pe alt fallback.
export function invoicePdfFilename (company, date) {
  const slug = fileSlug(company)
  const d = date ? ymd(date) : ""
  return slug && d ? `factura-${slug}-${d}.pdf` : ""
}

// Extrage numele fișierului din header-ul Content-Disposition trimis de BE
// (ex: `attachment; filename="Factura_ASP0022_..._LUNGU-TRANS-SRL.pdf"`).
// Întoarce "" dacă header-ul lipsește sau n-are filename.
export function filenameFromContentDisposition (headers) {
  const cd = (headers && (headers["content-disposition"] || headers["Content-Disposition"])) || ""
  const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
  return match && match[1] ? decodeURIComponent(match[1]) : ""
}

// "yyyy-MM-dd" -> "marți, 16 iunie 2026", using the app's lang.days/lang.months arrays.
// { withYear: false } drops the trailing year, e.g. for toast messages
// where the year is implied ("sâmbătă, 29 august").
export function formatDayLabel (iso, lang, { withYear = true } = {}) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '')
  if (!m) return ''
  const year = +m[1]
  const month = +m[2] - 1
  const day = +m[3]
  const dow = new Date(year, month, day).getDay()
  const dayName = lang.days[dow === 0 ? 6 : dow - 1]
  return withYear
    ? `${dayName}, ${day} ${lang.months[month]} ${year}`
    : `${dayName}, ${day} ${lang.months[month]}`
}

// { year, month (0-indexed), day } -> "yyyy-MM-dd"
export function isoDateForItem (item) {
  return `${item.year}-${String(item.month + 1).padStart(2, '0')}-${String(item.day).padStart(2, '0')}`
}

// "yyyy-MM-dd" -> true when strictly before today (calendar-day comparison,
// ignoring time-of-day) — zero-padded ISO strings sort correctly as text.
export function isPastIsoDate (iso) {
  return iso < ymd(new Date())
}

export const monthBounds = preset => {
  const base = preset === "this" ? new Date() : subMonths(new Date(), 1)
  return { start: startOfMonth(base), end: endOfMonth(base) }
}

export const isMonth = (range, preset) => {
  if (!range || !range.start || !range.end) return false
  const b = monthBounds(preset)
  return ymd(range.start) === ymd(b.start) && ymd(range.end) === ymd(b.end)
}

export function getEstimatedDate (days = 7) {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return `<div>${format(date, "dd.MM.yyyy")}</div>`
}

// ---------- strings ----------
export const norm = s => (s || "").toLowerCase().replace(/\s+/g, "")

// ---------- query params ----------
export const buildDateRangeParams = ({ dateRange, rar }) => ({
  ...(dateRange && dateRange.start && { from: ymd(dateRange.start) }),
  ...(dateRange && dateRange.end   && { to:   ymd(dateRange.end) }),
  ...(rar                          && { rar })
})

// ---------- calendar grid ----------
/**
 * Monday-start month grid: array of 7-cell weeks spanning the given month,
 * padded with adjacent-month days at both ends.
 * Each cell: { day: Number, monthType: 'prev' | 'current' | 'next' }.
 */
export function buildMonthWeeks (year, month) {
  const weeks = [];

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);

  let startDay = firstDayOfMonth.getDay();
  startDay = startDay === 0 ? 6 : startDay - 1;

  const daysInMonth = lastDayOfMonth.getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  let currentWeek = [];

  for (let i = startDay - 1; i >= 0; i--) {
    currentWeek.push({
      day: daysInPrevMonth - i,
      monthType: 'prev'
    });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push({
      day,
      monthType: 'current'
    });

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  if (currentWeek.length > 0) {
    let nextDay = 1;

    while (currentWeek.length < 7) {
      currentWeek.push({
        day: nextDay++,
        monthType: 'next'
      });
    }

    weeks.push(currentWeek);
  }

  return weeks;
}

export function resolveCellMonth (currentMonth, monthType) {
  if (monthType === 'prev') return currentMonth === 0 ? 11 : currentMonth - 1;
  if (monthType === 'next') return currentMonth === 11 ? 0 : currentMonth + 1;
  return currentMonth;
}

// { day, monthType } grid cell + the calendar's current month/year -> "yyyy-MM-dd"
export function isoDateForCell (currentMonth, currentYear, date) {
  const month = resolveCellMonth(currentMonth, date.monthType);
  let year = currentYear;
  if (date.monthType === 'prev' && currentMonth === 0) year -= 1;
  else if (date.monthType === 'next' && currentMonth === 11) year += 1;
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
}

// ---------- number parsing ----------
/**
 * Parse a value into a finite number, accepting `,` or `.` as decimal separator.
 * Returns 0 for null / undefined / empty / non-numeric input.
 */
export function _Parse_Number (value) {
  if (value === undefined || value === null || value === '') return 0
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  const n = parseFloat(String(value).replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

// ---------- meta ads attribution ----------
// Reads fbclid/fbp/fbc off a /comenzi redirect URL coming from the marketing
// site and returns the _fbp/_fbc cookie values to persist on this subdomain,
// so the pixel here reuses the click/browser ids already attributed there.
// Only returns keys for params actually present, so callers never clobber an
// existing cookie with a blank value on a normal (non-ad) navigation.
export function resolveMetaAdCookies (search, now = Date.now()) {
  const params = new URLSearchParams(search || '')
  const fbclid = params.get('fbclid')
  const fbp = params.get('fbp')
  const fbc = params.get('fbc') || (fbclid ? `fb.1.${now}.${fbclid}` : null)

  const cookies = {}
  if (fbp) cookies._fbp = fbp
  if (fbc) cookies._fbc = fbc
  return cookies
}
